import { type InfoCardItem, type InfoCardsWidgetProps } from '@fruitful/types';
import React, { useEffect } from 'react';
import { ScrollView, useWindowDimensions } from 'react-native';
import Animated, {
    Easing,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withTiming,
} from 'react-native-reanimated';
import { BodyMediumEmphasized, BodySmallRegular, View, YStack } from 'smoothie';

import DebtGraphSvg from '../../assets/images/debt-graph.svg';
import GuideZoomSvg from '../../assets/images/guide-zoom.svg';
import IncomeSplitGraphSvg from '../../assets/images/income-split-graph.svg';
import SpendGraphSvg from '../../assets/images/spend-graph.svg';
import { CHAT_COLUMN_MAX_WIDTH } from '../layout/ChatLayout';

const MIN_CARD_WIDTH = 220;
const CARD_GAP = 12;
const EDGE_PADDING = 30;

const ENTRANCE_STAGGER_MS = 130;
const ENTRANCE_DURATION_MS = 260;
const ENTRANCE_RISE_PX = 22;

const AnimatedYStack = Animated.createAnimatedComponent(YStack);

const PaycheckIllustration = () => (
    <View
        height={90}
        width="100%"
        overflow="hidden"
        alignItems="center"
        justifyContent="center"
    >
        <IncomeSplitGraphSvg width={184} height={90} />
    </View>
);

const DebtIllustration = () => (
    <View
        height={90}
        width="100%"
        overflow="hidden"
        alignItems="center"
        justifyContent="center"
    >
        <DebtGraphSvg width={214} height={90} />
    </View>
);

const SpendIllustration = () => (
    <View
        height={90}
        width="100%"
        overflow="hidden"
        alignItems="center"
        justifyContent="center"
    >
        <SpendGraphSvg width={206} height={90} />
    </View>
);

const GuidanceIllustration = () => (
    <View
        height={90}
        width="100%"
        overflow="hidden"
        alignItems="center"
        justifyContent="center"
    >
        <GuideZoomSvg width={206} height={90} />
    </View>
);

const CARD_ILLUSTRATIONS: Record<string, React.FC> = {
    'paycheck-has-a-job': PaycheckIllustration,
    'lower-debt-rates': DebtIllustration,
    'spend-with-confidence': SpendIllustration,
    'one-on-one-guidance': GuidanceIllustration,
};

const InfoCard = ({
    id,
    title,
    description,
    cardWidth,
    index,
    isLast,
    onEntranceComplete,
}: InfoCardItem & {
    cardWidth: number;
    index: number;
    isLast: boolean;
    onEntranceComplete?: () => void;
}) => {
    const Illustration = CARD_ILLUSTRATIONS[id];

    const entrance = useSharedValue(0);

    useEffect(() => {
        entrance.value = withDelay(
            index * ENTRANCE_STAGGER_MS,
            withTiming(
                1,
                {
                    duration: ENTRANCE_DURATION_MS,
                    easing: Easing.out(Easing.cubic),
                },
                (finished) => {
                    if (finished && isLast && onEntranceComplete != null) {
                        runOnJS(onEntranceComplete)();
                    }
                },
            ),
        );
    }, [entrance, index, isLast, onEntranceComplete]);

    const entranceStyle = useAnimatedStyle(() => ({
        opacity: entrance.value,
        transform: [{ translateY: (1 - entrance.value) * ENTRANCE_RISE_PX }],
    }));

    return (
        <AnimatedYStack
            style={entranceStyle}
            width={cardWidth}
            backgroundColor="rgba(255, 255, 255, 0.10)"
            borderRadius={16}
            borderWidth={1}
            borderColor="rgba(255, 255, 255, 0.40)"
            padding={16}
            overflow="hidden"
            justifyContent="space-between"
            gap={4}
        >
            <YStack gap={2}>
                <BodyMediumEmphasized color="$black">
                    {title}
                </BodyMediumEmphasized>
                <BodySmallRegular color="$gray1000">
                    {description}
                </BodySmallRegular>
            </YStack>
            {Illustration != null && <Illustration />}
        </AnimatedYStack>
    );
};

export const InfoCardsWidget = ({
    cards,
    onEntranceComplete,
}: InfoCardsWidgetProps & { onEntranceComplete?: () => void }) => {
    const { width: screenWidth } = useWindowDimensions();
    const chatWidth = Math.min(screenWidth, CHAT_COLUMN_MAX_WIDTH);
    const availableWidth = chatWidth - EDGE_PADDING * 2;
    const totalGaps = (cards.length - 1) * CARD_GAP;
    const fittedCardWidth = (availableWidth - totalGaps) / cards.length;
    const cardWidth = Math.max(MIN_CARD_WIDTH, Math.floor(fittedCardWidth));
    const shouldScroll = cardWidth * cards.length + totalGaps > availableWidth;

    return (
        <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            scrollEnabled={shouldScroll}
            contentContainerStyle={{
                gap: CARD_GAP,
                paddingHorizontal: EDGE_PADDING,
                ...(shouldScroll
                    ? {}
                    : { flexGrow: 1, justifyContent: 'center' }),
            }}
        >
            {cards.map((card, index) => (
                <InfoCard
                    key={card.id}
                    {...card}
                    cardWidth={cardWidth}
                    index={index}
                    isLast={index === cards.length - 1}
                    onEntranceComplete={onEntranceComplete}
                />
            ))}
        </ScrollView>
    );
};
